import { Injectable, inject } from '@angular/core';
import { DialogService } from '@tvd/design-system/dialog';
import type { ActiveDialog } from '@tvd/design-system/dialog';

export interface IntakeDrawerContext {
  orgId: string;
  branchId: string;
  staffId: string;
  /**
   * When set (e.g. opened from an intake notification), the drawer deep-links
   * straight to this thread instead of showing the list.
   */
  intakeId?: string;
}

/**
 * Opens the Intake drawer over the current screen.
 *
 * Mirrors `AiChatDrawerService`: `open(ctx)` lazy-`import()`s the drawer
 * container (so the dashboard stays fast for Staff Members who rarely screen)
 * and opens it via `DialogService.create(...).asDrawer(...).open()`, passing
 * org / branch / staff context as inputs. Closing the drawer returns the Staff
 * Member exactly where they were — the dialog service overlays the current
 * screen.
 */
@Injectable({ providedIn: 'root' })
export class IntakeDrawerService {
  readonly #dialog = inject(DialogService);
  #activeDialog: ActiveDialog<unknown> | undefined;

  readonly open = async (ctx: IntakeDrawerContext): Promise<void> => {
    // If already open, do not stack a second drawer.
    if (this.#activeDialog) return;

    const { IntakeDrawer } = await import('./intake-drawer');

    const ref = this.#dialog
      .create(IntakeDrawer, {
        inputs: {
          orgId: () => ctx.orgId,
          branchId: () => ctx.branchId,
          staffId: () => ctx.staffId,
          initialIntakeId: () => ctx.intakeId,
        },
        outputs: {
          close: () => this.close(),
        },
      })
      .asDrawer()
      .open();

    this.#activeDialog = ref;

    // Native dialog close fires on Escape, backdrop click, or programmatic close.
    ref.nativeElement.addEventListener(
      'close',
      () => {
        this.#activeDialog = undefined;
      },
      { once: true },
    );
  };

  readonly close = (): void => {
    if (this.#activeDialog) {
      this.#activeDialog.close();
      // Cleanup handled by the native close event listener.
    }
  };

  get isOpen(): boolean {
    return this.#activeDialog !== undefined;
  }
}
